import { useMemo } from 'react'
import { CheckCircle2, ClipboardList, UserRound } from 'lucide-react'
import type { CustomDataArtifact } from '@dev-streetbeat/chat/types'

import { Badge } from '@/components/ui/badge'

interface ArtifactProps<T> {
  artifact: T
  onLoaded?: () => void
}

interface TodoClient {
  client_code?: string
  client_label?: string
  bank_code?: string | null
  branch_name?: string | null
  total_eur?: number | null
}

interface ManagerTodo {
  todo_id: string
  status: string
  sort_order: number
  created_at?: string | null
  completed_at?: string | null
  source?: {
    source_label?: string
    source_type?: string
  }
  client: TodoClient
}

interface BankerTodos {
  user_id: number
  email: string
  name: string
  advisor_code: string
  active_count: number
  completed_count: number
  todos: ManagerTodo[]
}

interface ManagerTodosPayload {
  title: string
  summary: {
    banker_count: number
    active_count: number
    completed_count: number
  }
  bankers: BankerTodos[]
  operation?: {
    assigned_count?: number
    unassigned_count?: number
    skipped_existing_count?: number
    not_found_count?: number
    ambiguous_count?: number
  }
  as_of_dt?: string
}

function parsePayload(data: unknown): ManagerTodosPayload | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  const summary = raw.summary
  const bankersRaw = raw.bankers
  if (!summary || typeof summary !== 'object' || !Array.isArray(bankersRaw)) return null

  const bankers: BankerTodos[] = []
  for (const item of bankersRaw) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const userId = Number(row.user_id)
    const todosRaw = row.todos
    if (!Number.isFinite(userId) || !Array.isArray(todosRaw)) continue
    const todos: ManagerTodo[] = []
    for (const todoRaw of todosRaw) {
      if (!todoRaw || typeof todoRaw !== 'object') continue
      const todo = todoRaw as Record<string, unknown>
      const client = todo.client && typeof todo.client === 'object' ? (todo.client as TodoClient) : {}
      todos.push({
        todo_id: String(todo.todo_id || ''),
        status: String(todo.status || ''),
        sort_order: Number(todo.sort_order || 0),
        created_at: typeof todo.created_at === 'string' ? todo.created_at : null,
        completed_at: typeof todo.completed_at === 'string' ? todo.completed_at : null,
        source: todo.source && typeof todo.source === 'object' ? (todo.source as ManagerTodo['source']) : {},
        client,
      })
    }
    bankers.push({
      user_id: userId,
      email: String(row.email || ''),
      name: String(row.name || row.email || `Banker ${userId}`),
      advisor_code: String(row.advisor_code || ''),
      active_count: Number(row.active_count || 0),
      completed_count: Number(row.completed_count || 0),
      todos,
    })
  }

  const summaryRow = summary as Record<string, unknown>
  const operation = raw.operation && typeof raw.operation === 'object'
    ? (raw.operation as ManagerTodosPayload['operation'])
    : {}
  return {
    title: String(raw.title || 'Todo banker'),
    summary: {
      banker_count: Number(summaryRow.banker_count || bankers.length),
      active_count: Number(summaryRow.active_count || 0),
      completed_count: Number(summaryRow.completed_count || 0),
    },
    bankers,
    operation,
    as_of_dt: typeof raw.as_of_dt === 'string' ? raw.as_of_dt : undefined,
  }
}

function formatCurrency(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ''
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

function operationText(operation?: ManagerTodosPayload['operation']): string | null {
  if (!operation) return null
  if (typeof operation.assigned_count === 'number') {
    return `${operation.assigned_count} assegnati`
  }
  if (typeof operation.unassigned_count === 'number') {
    return `${operation.unassigned_count} rimossi`
  }
  return null
}

export function ManagerTodosArtifact({ artifact }: ArtifactProps<CustomDataArtifact>) {
  const payload = useMemo(() => parsePayload(artifact.custom_data?.data), [artifact])
  if (!payload) return null

  const opText = operationText(payload.operation)

  return (
    <section
      className="my-2 overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-sm"
      aria-label={payload.title}
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <ClipboardList className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <h4 className="truncate text-section-title text-foreground">{payload.title}</h4>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {opText ? <Badge variant="outline">{opText}</Badge> : null}
          <Badge variant="outline">{payload.summary.banker_count} banker</Badge>
          <Badge variant="outline">{payload.summary.active_count} attivi</Badge>
          <Badge variant="outline">{payload.summary.completed_count} completati</Badge>
        </div>
      </header>

      <div className="divide-y divide-border">
        {payload.bankers.length === 0 ? (
          <p className="m-0 px-4 py-4 text-caption text-muted-foreground">
            Nessun banker assegnato.
          </p>
        ) : (
          payload.bankers.map((banker) => (
            <section key={banker.user_id} className="grid gap-3 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <UserRound className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0">
                    <p className="m-0 truncate text-body-emphasis text-foreground">
                      {banker.name}
                    </p>
                    <p className="m-0 truncate text-caption text-muted-foreground">
                      {banker.advisor_code || banker.email}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline">{banker.active_count} attivi</Badge>
                  <Badge variant="outline">{banker.completed_count} completati</Badge>
                </div>
              </div>

              {banker.todos.length === 0 ? (
                <p className="m-0 text-caption text-muted-foreground">Nessun todo.</p>
              ) : (
                <ul className="m-0 grid list-none gap-2 p-0">
                  {banker.todos.map((todo) => {
                    const client = todo.client || {}
                    const aum = formatCurrency(client.total_eur)
                    return (
                      <li
                        key={todo.todo_id}
                        className="grid gap-1 rounded-md border border-border bg-muted/20 px-3 py-2"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-body-emphasis text-foreground">
                            {client.client_label || client.client_code || todo.todo_id}
                          </span>
                          <Badge variant="outline">
                            {todo.status === 'completed' ? (
                              <CheckCircle2 aria-hidden />
                            ) : null}
                            {todo.status}
                          </Badge>
                        </div>
                        <p className="m-0 text-caption text-muted-foreground">
                          {[client.branch_name, aum, todo.source?.source_label]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </li>
                    )
                  })}
                </ul>
              )}
            </section>
          ))
        )}
      </div>
    </section>
  )
}
